import type { Language } from './language';

/**
 * The same path under another language.
 *
 * Every page on this site but one has the same path in both languages, so switching language is
 * rewriting the leading `/kk` or `/ru`. The exception is an article, whose slug is per language by
 * construction — that page publishes its own alternates and the switcher uses those instead.
 *
 * **The query string and the fragment travel with it.** They were dropped once, and the page that
 * suffered is the activation screen: `/kk/account/activate?email=…&token=…` became
 * `/ru/account/activate` with nothing on it, so a customer who switched language was told their
 * invitation link was incomplete — and re-issuing it is the only recovery this product has. That
 * is why the caller passes the whole location rather than just `pathname`.
 *
 * Anchored at the start, which a bare `String.replace` is not: a path is only ever prefixed by its
 * language, and a search value that happens to read `/kk` must not be what gets rewritten.
 */
export function switchLanguagePath(path: string, from: Language, to: Language): string {
    if (path === `/${from}`) {
        return `/${to}`;
    }

    if (path.startsWith(`/${from}/`) || path.startsWith(`/${from}?`) || path.startsWith(`/${from}#`)) {
        return `/${to}${path.slice(from.length + 1)}`;
    }

    // A path with no language prefix — which the router redirects away from anyway. Sending the
    // visitor to that language's root is the honest answer; guessing at a rewrite is not.
    return `/${to}`;
}
